import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  FormControlLabel,
  Switch,
  Tooltip,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@material-ui/core";
import { useTheme, alpha } from "@material-ui/core/styles";
import { grey, yellow, orange, red } from "@material-ui/core/colors";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import InputMask from "react-input-mask";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import { CNPJ_MASK, isValidCnpj, onlyDigits, formatCnpj } from "../../utils/cnpj";

import {
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircleOutline as CheckCircleIcon,
  Visibility,
  VisibilityOff,
} from "@material-ui/icons";

import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import { head, isArray, has } from "lodash";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";

import moment from "moment";
import { i18n } from "../../translate/i18n";
import { isDarkTheme } from "../../helpers/darkTint";

// Atalho para as chaves desta tela (settings.companiesManager.*).
const t = (key, options) => i18n.t(`settings.companiesManager.${key}`, options);

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(2),
  },
  fullWidth: {
    width: "100%",
  },
  tableContainer: {
    width: "100%",
    overflowX: "scroll",
    ...theme.scrollbarStyles,
  },
  textfield: {
    width: "100%",
  },
  textRight: {
    textAlign: "right",
  },
  row: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  control: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },
}));

// Licença Empsis: só aparece nas instalações com EMPSIS_LICENCA_TOKEN no backend.
// Nas demais, GET /companies/licenses responde enabled: false e a tela fica
// igual à de antes.
const LICENSE_COLORS = {
  ok: "#2e7d32",
  blocked: "#d32f2f",
  exempt: "#1565c0",
  unknown: "#757575",
};

// Mesma regra do backend (CompanyLicenseService): sem CNPJ bloqueia mesmo
// isenta; isenta libera; inválida bloqueia; o resto libera.
const describeLicense = (info) => {
  if (info && !info.hasCnpj) {
    return { label: t("license.status.noCnpj"), color: LICENSE_COLORS.blocked };
  }
  if (info && info.exempt) {
    return {
      label:
        info.valid === false
          ? t("license.status.exemptInvalid")
          : t("license.status.exempt"),
      color: LICENSE_COLORS.exempt,
    };
  }
  if (info && info.valid === true) {
    return { label: t("license.status.valid"), color: LICENSE_COLORS.ok };
  }
  if (info && info.valid === false) {
    return { label: t("license.status.invalid"), color: LICENSE_COLORS.blocked };
  }
  return { label: t("license.status.unchecked"), color: LICENSE_COLORS.unknown };
};

const LicenseStatus = ({ info }) => {
  const { label, color } = describeLicense(info);
  const checkedAt = info?.checkedAt
    ? moment(info.checkedAt).format("L LT")
    : null;
  const details = [
    info?.message,
    checkedAt ? t("license.checkedAt", { date: checkedAt }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const showReason = info?.hasCnpj && info?.valid === false && info?.message;

  // Situação do token da Empsis, quando acrescenta algo ao motivo acima.
  let tokenLine = null;
  if (info?.autoRenew) {
    tokenLine = { text: t("license.token.autoRenew"), color: LICENSE_COLORS.ok };
  } else if (info?.hasToken === true) {
    tokenLine = { text: t("license.token.noAutoRenew"), color: LICENSE_COLORS.unknown };
  } else if (info?.hasToken === false && info?.valid !== false) {
    tokenLine = { text: t("license.token.missing"), color: LICENSE_COLORS.blocked };
  }

  const content = (
    <span>
      <span style={{ color, fontWeight: 600 }}>{label}</span>
      {showReason ? (
        <>
          <br />
          <small>{info.message}</small>
        </>
      ) : null}
      {tokenLine ? (
        <>
          <br />
          <small style={{ color: tokenLine.color }}>{tokenLine.text}</small>
        </>
      ) : null}
    </span>
  );

  return details ? <Tooltip title={details}>{content}</Tooltip> : content;
};

// Diálogo "Gerar token": pede usuário e senha da Empsis (CNPJ vem do
// formulário). A senha só passa por aqui; o backend a guarda cifrada.
const EmpsisTokenDialog = ({ open, onClose, initialCnpj, onGenerate }) => {
  const [cnpj, setCnpj] = useState("");
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    if (open) {
      setCnpj(formatCnpj(onlyDigits(initialCnpj || "")));
      setSenha("");
    }
  }, [open, initialCnpj]);

  const valido = onlyDigits(cnpj).length === 14 && usuario.trim() && senha;

  const handleGerar = async () => {
    setGerando(true);
    try {
      await onGenerate({ cnpj: onlyDigits(cnpj), usuario: usuario.trim(), senha });
      setSenha("");
      onClose();
    } catch (e) {
      toastError(e);
    }
    setGerando(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("tokenDialog.title")}</DialogTitle>
      <DialogContent>
        <small style={{ color: "#666" }}>{t("tokenDialog.description")}</small>
        <InputMask
          mask={CNPJ_MASK}
          maskChar={null}
          value={cnpj}
          onChange={(e) => setCnpj(e.target.value)}
        >
          {(inputProps) => (
            <TextField
              {...inputProps}
              label={t("fields.cnpj")}
              placeholder="00.000.000/0000-00"
              variant="outlined"
              margin="dense"
              fullWidth
            />
          )}
        </InputMask>
        <TextField
          label={t("tokenDialog.user")}
          variant="outlined"
          margin="dense"
          fullWidth
          autoComplete="off"
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
        />
        <TextField
          label={t("tokenDialog.password")}
          type="password"
          variant="outlined"
          margin="dense"
          fullWidth
          autoComplete="new-password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={gerando}>
          {t("tokenDialog.cancel")}
        </Button>
        <ButtonWithSpinner
          loading={gerando}
          disabled={gerando || !valido}
          onClick={handleGerar}
          variant="contained"
          color="primary"
        >
          {t("tokenDialog.generate")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export function CompanyForm(props) {
  const {
    onSubmit,
    onDelete,
    onCancel,
    initialValue,
    loading,
    licenseEnabled,
    license,
    licenseLoading,
    onCheckLicense,
    onToggleLicenseExempt,
    onGenerateEmpsisToken,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});
  const { user } = useContext(AuthContext);

  const [record, setRecord] = useState({
    name: "",
    cnpj: "",
    email: "",
    password: "",
    phone: "",
    planId: "",
    status: true,
    campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    customBaseValue: "",
    customUsers: "",
    customConnections: "",
    empsisApiToken: "",
    ...initialValue,
  });
  // Token gerado pelo diálogo numa empresa ainda não criada: usuário e senha
  // seguem junto no salvar, para o backend guardar e renovar sozinho.
  const [empsisDialogOpen, setEmpsisDialogOpen] = useState(false);
  // Token sempre começa oculto; o olho mostra só enquanto o usuário quiser.
  const [showEmpsisToken, setShowEmpsisToken] = useState(false);
  useEffect(() => {
    setShowEmpsisToken(false);
  }, [initialValue.id]);
  const [pendingEmpsisCreds, setPendingEmpsisCreds] = useState(null);

  const { listAdmin: listPlans } = usePlans();

  // Verifica se o usuário atual é o usuário ID 1
  const canEditCompanies = user && user.id === 1;

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecord((prev) => {
      if (moment(initialValue).isValid()) {
        initialValue.dueDate = moment(initialValue.dueDate).format(
          "YYYY-MM-DD"
        );
      }
      return {
        ...prev,
        ...initialValue,
      };
    });
  }, [initialValue]);

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    // personalização: campo vazio = null (usa o plano base)
    const toNullableNumber = (v) =>
      v === "" || v === null || v === undefined ? null : Number(v);
    const payload = {
      ...data,
      cnpj: onlyDigits(data.cnpj),
      customBaseValue: toNullableNumber(data.customBaseValue),
      customUsers: toNullableNumber(data.customUsers),
      customConnections: toNullableNumber(data.customConnections),
    };
    // a senha só faz sentido na criação, que é quando o admin é criado
    if (data.id) {
      delete payload.password;
    }
    // Token da Empsis: só vai quando a verificação de licença está ligada
    // (sem ela o campo nem aparece e o backend não deve mexer no token).
    if (!licenseEnabled) {
      delete payload.empsisApiToken;
    } else {
      payload.empsisApiToken = String(data.empsisApiToken || "").trim();
      if (
        !data.id &&
        pendingEmpsisCreds &&
        pendingEmpsisCreds.token === payload.empsisApiToken
      ) {
        // CNPJ do diálogo (pode diferir do da empresa): a renovação usa este
        payload.empsisCnpj = pendingEmpsisCreds.cnpj;
        payload.empsisUsuario = pendingEmpsisCreds.usuario;
        payload.empsisSenha = pendingEmpsisCreds.senha;
      }
    }
    setPendingEmpsisCreds(null);
    onSubmit(payload);
    setRecord({ ...initialValue, dueDate: "" });
  };

  // Na edição o backend já salva o token (e as credenciais) na hora; na criação
  // só preenche o campo e guarda as credenciais para irem junto no salvar.
  const handleGenerateEmpsisToken = async (creds, formik) => {
    const data = await onGenerateEmpsisToken({
      ...creds,
      companyId: record.id,
    });
    formik.setFieldValue("empsisApiToken", data.token || "");
    if (record.id === undefined) {
      setPendingEmpsisCreds({ token: data.token, ...creds });
      toast.success(t("toasts.tokenGeneratedPending"));
    } else {
      toast.success(t("toasts.tokenGeneratedSaved"));
    }
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: {
          companyId: initialValue.id,
        },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate)
            .add(1, "month")
            .format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(2, "month")
            .format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(3, "month")
            .format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(6, "month")
            .format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate)
            .add(12, "month")
            .format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    }
    setRecord(data);
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        className={classes.fullWidth}
        initialValues={record}
        validationSchema={Yup.object().shape({
          // CNPJ obrigatório ao criar; ao editar empresa legada (sem CNPJ) não trava,
          // mas se preenchido precisa ser válido.
          cnpj: record.id
            ? Yup.string().test(
                "valid-cnpj",
                t("validation.cnpjInvalid"),
                (value) => !value || isValidCnpj(value)
              )
            : Yup.string()
                .required(t("validation.cnpjRequired"))
                .test("valid-cnpj", t("validation.cnpjInvalid"), (value) =>
                  isValidCnpj(value)
                ),
          // Senha só é exigida na criação, quando o usuário admin é criado junto.
          password: record.id
            ? Yup.string()
            : Yup.string()
                .required(t("validation.passwordRequired"))
                .min(5, t("validation.passwordMin")),
          // Token da Empsis: obrigatório na criação com a licença ligada.
          empsisApiToken:
            licenseEnabled && !record.id
              ? Yup.string().trim().required(t("validation.empsisTokenRequired"))
              : Yup.string(),
        })}
        onSubmit={(values, { resetForm }) =>
          setTimeout(() => {
            handleSubmit(values);
            resetForm();
          }, 500)
        }
      >
        {(values, setValues) => (
          <Form className={classes.fullWidth}>
            <Grid spacing={2} justifyContent="flex-start" container>
              {/* Linha 1: Nome, CNPJ, E-mail e Senha do administrador */}
              <Grid xs={12} sm={6} md={4} item>
                <Field
                  as={TextField}
                  label={t("fields.name")}
                  name="name"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              <Grid xs={12} sm={6} md={3} item>
                <Field name="cnpj">
                  {({ field }) => (
                    <InputMask {...field} mask={CNPJ_MASK} maskChar={null}>
                      {(inputProps) => (
                        <TextField
                          {...inputProps}
                          label={t("fields.cnpj")}
                          placeholder="00.000.000/0000-00"
                          variant="outlined"
                          className={classes.fullWidth}
                          margin="dense"
                          required
                          error={
                            values.touched.cnpj && Boolean(values.errors.cnpj)
                          }
                          helperText={values.touched.cnpj && values.errors.cnpj}
                        />
                      )}
                    </InputMask>
                  )}
                </Field>
              </Grid>
              <Grid xs={12} sm={6} md={3} item>
                <Field
                  as={TextField}
                  label={t("fields.email")}
                  name="email"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  required
                />
              </Grid>
              {/* Só na criação: o backend cria o usuário admin da empresa com
                  este e-mail e esta senha. Sem ela o cadastro falhava, porque
                  Users.passwordHash é NOT NULL. Na edição não aparece — a senha
                  do admin se altera pela tela de Usuários. */}
              {!record.id && (
                <Grid xs={12} sm={6} md={2} item>
                  <Field
                    as={TextField}
                    label={t("fields.adminPassword")}
                    name="password"
                    type="password"
                    variant="outlined"
                    className={classes.fullWidth}
                    margin="dense"
                    required
                  />
                </Grid>
              )}
              <Grid xs={12} item style={{ padding: 0 }} />
              {/* Linha 2: Telefone, Plano, Status e Campanhas */}
              <Grid xs={12} sm={6} md={3} item>
                <Field
                  as={TextField}
                  label={t("fields.phone")}
                  name="phone"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              <Grid xs={12} sm={6} md={3} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="plan-selection">{t("fields.plan")}</InputLabel>
                  <Field
                    as={Select}
                    id="plan-selection"
                    label={t("fields.plan")}
                    labelId="plan-selection-label"
                    name="planId"
                    margin="dense"
                    required
                  >
                    {plans.map((plan, key) => (
                      <MenuItem key={key} value={plan.id}>
                        {plan.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={3} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="status-selection">{t("fields.status")}</InputLabel>
                  <Field
                    as={Select}
                    id="status-selection"
                    label={t("fields.status")}
                    labelId="status-selection-label"
                    name="status"
                    margin="dense"
                  >
                    <MenuItem value={true}>{t("options.active")}</MenuItem>
                    <MenuItem value={false}>{t("options.inactive")}</MenuItem>
                  </Field>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={3} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="status-selection">{t("fields.campaigns")}</InputLabel>
                  <Field
                    as={Select}
                    id="campaigns-selection"
                    label={t("fields.campaigns")}
                    labelId="campaigns-selection-label"
                    name="campaignsEnabled"
                    margin="dense"
                  >
                    <MenuItem value={true}>{t("options.enabled")}</MenuItem>
                    <MenuItem value={false}>{t("options.disabled")}</MenuItem>
                  </Field>
                </FormControl>
              </Grid>
              <Grid xs={12} item style={{ padding: 0 }} />
              {/* Linha 3: Vencimento, Recorrência e Token Empsis (com licença ligada) */}
              <Grid xs={12} sm={6} md={2} item>
                <FormControl variant="outlined" fullWidth>
                  <Field
                    as={TextField}
                    label={t("fields.dueDate")}
                    type="date"
                    name="dueDate"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="recorrencia-selection">
                    {t("fields.recurrence")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={t("fields.recurrence")}
                    labelId="recorrencia-selection-label"
                    id="recurrence"
                    name="recurrence"
                    margin="dense"
                  >
                    <MenuItem value="MENSAL">{t("recurrence.MENSAL")}</MenuItem>
                    {/*<MenuItem value="BIMESTRAL">Bimestral</MenuItem>*/}
                    {/*<MenuItem value="TRIMESTRAL">Trimestral</MenuItem>*/}
                    {/*<MenuItem value="SEMESTRAL">Semestral</MenuItem>*/}
                    {/*<MenuItem value="ANUAL">Anual</MenuItem>*/}
                  </Field>
                </FormControl>
              </Grid>
              {licenseEnabled && (
                <>
                  <Grid xs={12} sm={8} md={4} item>
                    <Field
                      as={TextField}
                      label={t("fields.empsisToken")}
                      name="empsisApiToken"
                      type={showEmpsisToken ? "text" : "password"}
                      variant="outlined"
                      className={classes.fullWidth}
                      margin="dense"
                      required={record.id === undefined}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label={
                                showEmpsisToken
                                  ? t("tokenVisibility.hide")
                                  : t("tokenVisibility.show")
                              }
                              onClick={() => setShowEmpsisToken((prev) => !prev)}
                            >
                              {showEmpsisToken ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={
                        values.touched.empsisApiToken &&
                        Boolean(values.errors.empsisApiToken)
                      }
                      helperText={
                        (values.touched.empsisApiToken &&
                          values.errors.empsisApiToken) ||
                        t("fields.empsisTokenHelper")
                      }
                    />
                  </Grid>
                  {canEditCompanies && (
                    <Grid xs={12} sm={4} md={2} item>
                      <ButtonWithSpinner
                        className={classes.fullWidth}
                        style={{ marginTop: 8 }}
                        loading={false}
                        onClick={() => setEmpsisDialogOpen(true)}
                        variant="outlined"
                        color="primary"
                      >
                        {t("buttons.generateToken")}
                      </ButtonWithSpinner>
                    </Grid>
                  )}
                  <EmpsisTokenDialog
                    open={empsisDialogOpen}
                    onClose={() => setEmpsisDialogOpen(false)}
                    initialCnpj={values.values.cnpj}
                    onGenerate={(creds) => handleGenerateEmpsisToken(creds, values)}
                  />
                  <Grid xs={12} item style={{ padding: 0 }} />
                </>
              )}
              {licenseEnabled && record.id !== undefined && (
                <>
                  <Grid xs={12} item style={{ paddingBottom: 0 }}>
                    <small style={{ color: "#666" }}>{t("license.section")}</small>
                  </Grid>
                  <Grid
                    xs={12}
                    sm={6}
                    md={3}
                    item
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <LicenseStatus info={license} />
                  </Grid>
                  {canEditCompanies && (
                    <>
                      <Grid xs={12} sm={6} md={4} item>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!license?.exempt}
                              onChange={(e) =>
                                onToggleLicenseExempt(record.id, e.target.checked)
                              }
                              disabled={licenseLoading}
                              color="primary"
                            />
                          }
                          label={t("license.exemptLabel")}
                        />
                        {license && !license.hasCnpj && (
                          <div>
                            <small style={{ color: LICENSE_COLORS.blocked }}>
                              {t("license.exemptNoCnpjHint")}
                            </small>
                          </div>
                        )}
                      </Grid>
                      <Grid xs={12} sm={6} md={2} item>
                        <ButtonWithSpinner
                          className={classes.fullWidth}
                          loading={licenseLoading}
                          onClick={() => onCheckLicense(record.id)}
                          variant="outlined"
                          color="primary"
                        >
                          {t("buttons.recheckLicense")}
                        </ButtonWithSpinner>
                      </Grid>
                    </>
                  )}
                  <Grid xs={12} item style={{ padding: 0 }} />
                </>
              )}
              {canEditCompanies && (
                <>
                  <Grid xs={12} item style={{ paddingBottom: 0 }}>
                    <small style={{ color: "#666" }}>
                      {t("customization")}
                    </small>
                  </Grid>
                  <Grid xs={12} sm={6} md={2} item>
                    <Field
                      as={TextField}
                      label={t("fields.customValue")}
                      name="customBaseValue"
                      type="number"
                      inputProps={{ min: 0, step: "0.01" }}
                      variant="outlined"
                      className={classes.fullWidth}
                      margin="dense"
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={2} item>
                    <Field
                      as={TextField}
                      label={t("fields.customUsers")}
                      name="customUsers"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      variant="outlined"
                      className={classes.fullWidth}
                      margin="dense"
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={2} item>
                    <Field
                      as={TextField}
                      label={t("fields.customConnections")}
                      name="customConnections"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      variant="outlined"
                      className={classes.fullWidth}
                      margin="dense"
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={6} item />
                </>
              )}
              <Grid xs={12} item>
                {canEditCompanies && (
                  <Grid justifyContent="flex-end" spacing={1} container>
                    <Grid xs={4} md={1} item>
                      <ButtonWithSpinner
                        className={classes.fullWidth}
                        style={{ marginTop: 7 }}
                        loading={loading}
                        onClick={() => onCancel()}
                        variant="contained"
                      >
                        {t("buttons.clear")}
                      </ButtonWithSpinner>
                    </Grid>
                    {record.id !== undefined ? (
                      <>
                        <Grid xs={6} md={1} item>
                          <ButtonWithSpinner
                            style={{ marginTop: 7 }}
                            className={classes.fullWidth}
                            loading={loading}
                            onClick={() => onDelete(record)}
                            variant="contained"
                            color="secondary"
                          >
                            {t("buttons.delete")}
                          </ButtonWithSpinner>
                        </Grid>
                        <Grid xs={6} md={2} item>
                          <ButtonWithSpinner
                            style={{ marginTop: 7 }}
                            className={classes.fullWidth}
                            loading={loading}
                            onClick={() => incrementDueDate()}
                            variant="contained"
                            color="primary"
                          >
                            {t("buttons.addDueDate")}
                          </ButtonWithSpinner>
                        </Grid>
                        <Grid xs={6} md={1} item>
                          <ButtonWithSpinner
                            style={{ marginTop: 7 }}
                            className={classes.fullWidth}
                            loading={loading}
                            onClick={() => handleOpenModalUsers()}
                            variant="contained"
                            color="primary"
                          >
                            {t("buttons.user")}
                          </ButtonWithSpinner>
                        </Grid>
                      </>
                    ) : null}
                    <Grid xs={6} md={1} item>
                      <ButtonWithSpinner
                        className={classes.fullWidth}
                        style={{ marginTop: 7 }}
                        loading={loading}
                        type="submit"
                        variant="contained"
                        color="primary"
                      >
                        {t("buttons.save")}
                      </ButtonWithSpinner>
                    </Grid>
                  </Grid>
                )}
                {!canEditCompanies && (
                  <Grid justifyContent="center" spacing={1} container>
                    <Grid xs={12} item>
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '20px', 
                        color: isDark ? theme.palette.text.secondary : '#666',
                        backgroundColor: isDark ? theme.palette.action.hover : '#f5f5f5',
                        borderRadius: '8px',
                        border: `1px solid ${isDark ? theme.palette.divider : '#ddd'}`
                      }}>
                        <strong>{t("restricted.title")}</strong>
                        <br />
                        {t("restricted.message")}
                        <br />
                        <small>{t("restricted.hint")}</small>
                      </div>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
}

export function CompaniesManagerGrid(props) {
  const {
    records,
    onSelect,
    onToggleStatus,
    licenseEnabled,
    licenseMode,
    licenses,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const { dateToClient } = useDate();
  const { user } = useContext(AuthContext);

  const renderStatus = (row) => {
    return row.status === false ? (
      <span style={{ color: isDark ? red[300] : "#d32f2f", fontWeight: 600 }}>
        {t("options.inactive")}
      </span>
    ) : (
      t("options.active")
    );
  };

  const renderPlan = (row) => {
    return row.planId !== null ? row.plan.name : "-";
  };

  const renderCampaignsStatus = (row) => {
    if (
      has(row, "settings") &&
      isArray(row.settings) &&
      row.settings.length > 0
    ) {
      const setting = row.settings.find((s) => s.key === "campaignsEnabled");
      if (setting) {
        return setting.value === "true"
          ? t("options.enabled")
          : t("options.disabled");
      }
    }
    return t("options.disabled");
  };

  // No tema escuro os fundos claros viram tons translúcidos (o texto da linha é branco)
  const rowStyle = (record) => {
    // Empresa inativada: destaque cinza tem prioridade sobre alertas de vencimento
    if (record.status === false) {
      return { backgroundColor: isDark ? alpha(grey[500], 0.2) : "#ececec", opacity: 0.7 };
    }
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff === 5) {
        return { backgroundColor: isDark ? alpha(yellow[500], 0.2) : "#fffead" };
      }
      if (diff >= -3 && diff <= 4) {
        return { backgroundColor: isDark ? alpha(orange[500], 0.2) : "#f7cc8f" };
      }
      if (diff === -4) {
        return { backgroundColor: isDark ? alpha(red[500], 0.2) : "#fa8c8c" };
      }
    }
    return {};
  };

  // Verifica se o usuário atual é o usuário ID 1
  const canEditCompanies = user && user.id === 1;

  return (
    <Paper className={classes.tableContainer}>
      <Table
        className={classes.fullWidth}
        size="small"
        aria-label="a dense table"
      >
        <TableHead>
          <TableRow>
            {canEditCompanies && (
              <TableCell align="center" style={{ width: "1%" }}>
                #
              </TableCell>
            )}
            <TableCell align="left">{t("fields.name")}</TableCell>
            <TableCell align="left">{t("fields.cnpj")}</TableCell>
            <TableCell align="left">{t("fields.email")}</TableCell>
            <TableCell align="left">{t("fields.phone")}</TableCell>
            <TableCell align="left">{t("fields.plan")}</TableCell>
            <TableCell align="left">{t("fields.campaigns")}</TableCell>
            <TableCell align="left">{t("fields.status")}</TableCell>
            {licenseEnabled && (
              <TableCell align="left">
                {licenseMode === "monitor" ? (
                  <Tooltip title={t("license.monitorTooltip")}>
                    <span>{t("license.columnMonitor")}</span>
                  </Tooltip>
                ) : (
                  t("license.column")
                )}
              </TableCell>
            )}
            <TableCell align="left">{t("grid.createdAt")}</TableCell>
            <TableCell align="left">{t("grid.dueDate")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow style={rowStyle(row)} key={key}>
              {canEditCompanies && (
                <TableCell
                  align="center"
                  style={{ width: "1%", whiteSpace: "nowrap" }}
                >
                  <IconButton onClick={() => onSelect(row)} aria-label="edit">
                    <EditIcon />
                  </IconButton>
                  {row.status === false ? (
                    <Tooltip title={t("grid.reactivate")}>
                      <IconButton
                        onClick={() => onToggleStatus(row)}
                        aria-label="activate"
                      >
                        <CheckCircleIcon style={{ color: "#4caf50" }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t("grid.deactivate")}>
                      <IconButton
                        onClick={() => onToggleStatus(row)}
                        aria-label="deactivate"
                      >
                        <BlockIcon style={{ color: "#d32f2f" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              )}
              <TableCell align="left">{row.name || "-"}</TableCell>
              <TableCell align="left">
                {row.cnpj ? formatCnpj(row.cnpj) : "-"}
              </TableCell>
              <TableCell align="left">{row.email || "-"}</TableCell>
              <TableCell align="left">{row.phone || "-"}</TableCell>
              <TableCell align="left">{renderPlan(row)}</TableCell>
              <TableCell align="left">{renderCampaignsStatus(row)}</TableCell>
              <TableCell align="left">{renderStatus(row)}</TableCell>
              {licenseEnabled && (
                <TableCell align="left">
                  <LicenseStatus info={(licenses || {})[row.id]} />
                </TableCell>
              )}
              <TableCell align="left">{dateToClient(row.createdAt)}</TableCell>
              <TableCell align="left">
                {dateToClient(row.dueDate)}
                <br />
                <span>
                  {row.recurrence
                    ? t(`recurrence.${row.recurrence}`, {
                        defaultValue: row.recurrence,
                      })
                    : ""}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function CompaniesManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  // Por padrão exibe apenas empresas ativas; o switch "Ver inativas" inclui as demais
  const [showInactive, setShowInactive] = useState(false);
  const [toggleStatusRecord, setToggleStatusRecord] = useState(null);
  const [licenseState, setLicenseState] = useState({
    enabled: false,
    mode: "off",
    byCompany: {},
  });
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [record, setRecord] = useState({
    name: "",
    cnpj: "",
    email: "",
    password: "",
    phone: "",
    planId: "",
    status: true,
    campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    customBaseValue: "",
    customUsers: "",
    customConnections: "",
    empsisApiToken: "",
  });

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLicenses = async () => {
    try {
      const { data } = await api.get("/companies/licenses");
      if (!data || !data.enabled) {
        setLicenseState({ enabled: false, mode: "off", byCompany: {} });
        return;
      }
      const byCompany = {};
      (data.items || []).forEach((item) => {
        byCompany[item.companyId] = item;
      });
      setLicenseState({ enabled: true, mode: data.mode, byCompany });
    } catch (e) {
      // Instalação sem a validação (ou backend antigo sem a rota): a tela segue
      // sem a coluna de licença, sem incomodar com toast.
      setLicenseState({ enabled: false, mode: "off", byCompany: {} });
    }
  };

  const updateLicenseEntry = (item) => {
    if (!item || item.companyId === undefined) return;
    setLicenseState((prev) => ({
      ...prev,
      byCompany: { ...prev.byCompany, [item.companyId]: item },
    }));
  };

  const handleCheckLicense = async (companyId) => {
    setLicenseLoading(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/license/check`);
      updateLicenseEntry(data);
      if (data.unavailable) {
        toast.warn(
          t("toasts.licenseUnavailable", {
            reason: data.unavailableMessage || t("toasts.noDetails"),
          })
        );
      } else {
        const { label } = describeLicense(data);
        toast.info(
          data.message
            ? t("toasts.licenseRecheckedWithReason", {
                status: label,
                reason: data.message,
              })
            : t("toasts.licenseRechecked", { status: label })
        );
      }
    } catch (e) {
      toastError(e);
    }
    setLicenseLoading(false);
  };

  // Gerar token pela tela Empresas. Com companyId (edição) o backend salva e
  // revalida a licença; sem (criação) só devolve o token. Erros sobem para o
  // diálogo, que mostra o toast e continua aberto.
  const handleGenerateEmpsisToken = async (payload) => {
    const { data } = await api.post("/companies/empsis-token/gerar", payload);
    if (data && data.license) {
      updateLicenseEntry(data.license);
    }
    return data;
  };

  const handleToggleLicenseExempt = async (companyId, exempt) => {
    setLicenseLoading(true);
    try {
      const { data } = await api.put(`/companies/${companyId}/license`, {
        exempt,
      });
      updateLicenseEntry(data);
      toast.success(
        exempt
          ? t("toasts.exemptOn")
          : t("toasts.exemptOff")
      );
    } catch (e) {
      toastError(e);
    }
    setLicenseLoading(false);
  };

  const loadPlans = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      setRecords(companyList);
    } catch (e) {
      toast.error(t("toasts.loadError"));
    }
    await loadLicenses();
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success(t("toasts.success"));
    } catch (e) {
      // antes qualquer falha virava a mesma frase genérica, o que escondia a
      // causa real (e-mail já em uso, senha ausente, CNPJ duplicado...)
      toastError(e);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success(t("toasts.success"));
    } catch (e) {
      toast.error(t("toasts.error"));
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord((prev) => ({
      ...prev,
      id: undefined,
      name: "",
      cnpj: "",
      email: "",
      password: "",
      phone: "",
      planId: "",
      status: true,
      campaignsEnabled: false,
      dueDate: "",
      recurrence: "",
      customBaseValue: "",
      customUsers: "",
      customConnections: "",
      empsisApiToken: "",
    }));
  };

  const handleToggleStatus = (row) => {
    setToggleStatusRecord(row);
  };

  const handleConfirmToggleStatus = async () => {
    if (!toggleStatusRecord) return;
    const newStatus = toggleStatusRecord.status === false;
    setLoading(true);
    try {
      // Update parcial: o backend ignora campos undefined
      await update({ id: toggleStatusRecord.id, status: newStatus });
      await loadPlans();
      toast.success(
        newStatus
          ? t("toasts.reactivated")
          : t("toasts.deactivated")
      );
    } catch (e) {
      toast.error(t("toasts.statusError"));
    }
    setToggleStatusRecord(null);
    setLoading(false);
  };

  const handleSelect = (data) => {
    let campaignsEnabled = false;

    const setting = data.settings.find(
      (s) => s.key.indexOf("campaignsEnabled") > -1
    );
    if (setting) {
      campaignsEnabled =
        setting.value === "true" || setting.value === "enabled";
    }

    const profile = data.planProfile || {};
    const empsisTokenSetting = (data.settings || []).find(
      (s) => s.key === "empsisApiToken"
    );
    setRecord((prev) => ({
      ...prev,
      id: data.id,
      empsisApiToken: (empsisTokenSetting && empsisTokenSetting.value) || "",
      name: data.name || "",
      cnpj: data.cnpj || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      campaignsEnabled,
      dueDate: data.dueDate || "",
      recurrence: data.recurrence || "",
      customBaseValue:
        profile.customBaseValue !== null &&
        profile.customBaseValue !== undefined
          ? profile.customBaseValue
          : "",
      customUsers:
        profile.customUsers !== null && profile.customUsers !== undefined
          ? profile.customUsers
          : "",
      customConnections:
        profile.customConnections !== null &&
        profile.customConnections !== undefined
          ? profile.customConnections
          : "",
    }));
  };

  // "só status === false significa inativa" — mesma semântica do backend
  const inactiveCount = records.filter((r) => r.status === false).length;
  const displayedRecords = showInactive
    ? records
    : records.filter((r) => r.status !== false);

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <CompanyForm
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            licenseEnabled={licenseState.enabled}
            license={
              record.id !== undefined ? licenseState.byCompany[record.id] : null
            }
            licenseLoading={licenseLoading}
            onCheckLicense={handleCheckLicense}
            onToggleLicenseExempt={handleToggleLicenseExempt}
            onGenerateEmpsisToken={handleGenerateEmpsisToken}
          />
        </Grid>
        <Grid xs={12} item style={{ textAlign: "right", paddingBottom: 0 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={t("showInactive", { count: inactiveCount })}
          />
        </Grid>
        <Grid xs={12} item>
          <CompaniesManagerGrid
            records={displayedRecords}
            onSelect={handleSelect}
            onToggleStatus={handleToggleStatus}
            licenseEnabled={licenseState.enabled}
            licenseMode={licenseState.mode}
            licenses={licenseState.byCompany}
          />
        </Grid>
      </Grid>
      <ConfirmationModal
        title={t("confirm.deleteTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {t("confirm.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={
          toggleStatusRecord?.status === false
            ? t("confirm.reactivateTitle", { name: toggleStatusRecord?.name || "" })
            : t("confirm.deactivateTitle", { name: toggleStatusRecord?.name || "" })
        }
        open={!!toggleStatusRecord}
        onClose={() => setToggleStatusRecord(null)}
        onConfirm={handleConfirmToggleStatus}
      >
        {toggleStatusRecord?.status === false
          ? t("confirm.reactivateMessage")
          : t("confirm.deactivateMessage")}
      </ConfirmationModal>
    </Paper>
  );
}
